import React from "react"
import {useFormik} from "formik";
import * as yup from "yup";
import {startOfDay, startOfToday} from "date-fns";

import {isValidDateObject} from "../../../services/date/validators";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField/TextField";
import Typography from "@mui/material/Typography";
import {DatePicker} from "@mui/x-date-pickers";


export const useStepDetails = ({adults, children, arrival, departure, petsNumber, onSubmit = f => f}) => {
    const arrivalSec = Date.parse(arrival);
    const departureSec = Date.parse(departure);

    const formik = useFormik({
            initialValues: {
                adults: adults || 2,
                children: children || 0,
                arrival: arrivalSec ? new Date(arrivalSec) : null,
                departure: departureSec ? new Date(departureSec) : null,
                petsNumber: petsNumber || 0,
            },
            validationSchema: yup.object().shape({
                arrival: yup
                    .date()
                    .typeError('Deve essere una data valida gg/mm/aaaa')
                    .required('Campo obbligatorio'),
                departure: yup
                    .date()
                    .typeError('Deve essere una data valida gg/mm/aaaa\'')
                    .min(startOfToday(), 'Deve essere maggiore o uguale alla data di oggi')
                    .test(
                        'al-least-one-night',
                        'Deve essere maggiore rispetto alla data di arrivo',
                        (value, testContext) => {
                            const {arrival} = testContext.parent;
                            return startOfDay(value) > startOfDay(arrival);
                        }
                    )
                    .required('Campo obbligatorio'),
                adults: yup
                    .number()
                    .positive('Deve essere un numero positivo')
                    .integer('Deve essere un numero intero')
                    .typeError('Deve essere un numero')
                    .required('Campo obbligatorio'),
                children: yup
                    .number()
                    .integer('Deve essere un numero intero')
                    .min(0, 'Deve essere maggiore o uguale a 0')
                    .typeError('Deve essere un numero')
                    .required('Campo obbligatorio'),
                petsNumber: yup
                    .number()
                    .integer('Deve essere un numero intero')
                    .min(0, 'Deve essere maggiore o uguale a 0')
                    .max(5, 'Deve essere maggiore o uguale a 5')
                    .typeError('Deve essere un numero')
                    .required('Campo obbligatorio'),
            }),
            onSubmit: (values) => {
                const {arrival, departure, ...data} = values;  // remove arrival and departure
                onSubmit(data);
            },
        }
    );

    return {formik};
}

const StepDetails = ({formik}) => {

    return <>
        <Typography variant='h6' color='primary' fontWeight="normal" sx={{mb: 2}}>
            Dettagli
        </Typography>

        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <DatePicker value={formik.values.arrival}
                            onChange={date => formik.setFieldValue("arrival", date)}
                            disablePast
                            label="Data arrivo"
                            closeOnSelect
                            disableHighlightToday
                            showDaysOutsideCurrentMonth
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    required: true,
                                    error: Boolean(formik.touched.arrival) && Boolean(formik.errors.arrival),
                                    helperText: formik.touched.arrival && formik.errors.arrival
                                }
                            }}/>
            </Grid>

            <Grid item xs={12} md={6}>
                <DatePicker value={formik.values.departure}
                            onChange={date => formik.setFieldValue("departure", date)}
                            disablePast
                            minDate={formik.values.arrival}
                            defaultCalendarMonth={isValidDateObject(formik.values.arrival) ? formik.values.arrival : undefined}
                            label="Data partenza"
                            closeOnSelect
                            disableHighlightToday
                            showDaysOutsideCurrentMonth
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    required: true,
                                    error: Boolean(formik.touched.departure) && Boolean(formik.errors.departure),
                                    helperText: formik.touched.departure && formik.errors.departure
                                }
                            }}/>
            </Grid>

            <Grid item xs={12} sx={{mt: 2}}>
                <TextField type="number"
                           label="Numero adulti"
                           fullWidth
                           name="adults"
                           required
                           value={formik.values.adults}
                           onChange={formik.handleChange}
                           error={formik.touched.adults && Boolean(formik.errors.adults)}
                           helperText={formik.touched.adults && formik.errors.adults}/>
            </Grid>

            <Grid item xs={12}>
                <TextField type="number"
                           label="Numero bambini"
                           fullWidth
                           name="children"
                           required
                           value={formik.values.children}
                           onChange={formik.handleChange}
                           error={formik.touched.children && Boolean(formik.errors.children)}
                           helperText={formik.touched.children && formik.errors.children}/>
            </Grid>

            <Grid item xs={12}>
                <TextField type="number"
                           label="Numero Animali Domestici"
                           fullWidth
                           name="petsNumber"
                           required
                           value={formik.values.petsNumber}
                           onChange={formik.handleChange}
                           error={formik.touched.petsNumber && Boolean(formik.errors.petsNumber)}
                           helperText={formik.touched.petsNumber && formik.errors.petsNumber}/>
            </Grid>
        </Grid>
    </>
}

export default StepDetails;